import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../App.css';
import './LoginPage.css';
import { useAuthContext } from '../hooks/AuthContext';
import { resolvePostAuthRoute } from '../utils/authRedirect';
import EnvironmentDetector from '../utils/EnvironmentDetector';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SECONDS = 30;
const STAGING_DEFAULT_EMAIL = 'pmgallini+1@gmail.com';

function getDefaultLoginEmail() {
  return EnvironmentDetector.detectEnvironment() === 'staging'
    ? STAGING_DEFAULT_EMAIL
    : '';
}

function maskEmail(email) {
  if (!email || !email.includes('@')) {
    return email;
  }

  const [localPart, domain] = email.split('@');
  const visibleLocal = localPart.slice(0, 2);
  const maskedLocal = `${visibleLocal}${'•'.repeat(Math.max(localPart.length - visibleLocal.length, 1))}`;
  return `${maskedLocal}@${domain}`;
}

function toFriendlyAuthError(error, fallbackMessage) {
  const rawMessage = (error?.message || '').toLowerCase();

  if (!rawMessage) {
    return fallbackMessage;
  }

  if (rawMessage.includes('invalid email')) {
    return 'Enter a valid email address.';
  }

  if (
    rawMessage.includes('security purposes') ||
    rawMessage.includes('rate limit') ||
    rawMessage.includes('too many requests') ||
    rawMessage.includes('over_email_send_rate_limit')
  ) {
    return 'Please wait a bit before requesting another code.';
  }

  if (rawMessage.includes('invalid token') || rawMessage.includes('invalid otp') || rawMessage.includes('token')) {
    return 'That code doesn’t look right. Check it and try again.';
  }

  if (rawMessage.includes('expired')) {
    return 'That code has expired. Request a new one and try again.';
  }

  if (rawMessage.includes('network') || rawMessage.includes('fetch')) {
    return 'We could not reach the server. Check your connection and try again.';
  }

  return fallbackMessage;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    authenticatedUser,
    authError,
    authLoading,
    hasSupabaseConfig,
    requestOtp,
    resendOtp,
    verifyOtp,
  } = useAuthContext();

  const [email, setEmail] = useState(getDefaultLoginEmail);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [authStep, setAuthStep] = useState('email');

  const maskedEmail = useMemo(() => maskEmail(submittedEmail), [submittedEmail]);

  useEffect(() => {
    if (authenticatedUser) {
      navigate(resolvePostAuthRoute(location.state), { replace: true });
    }
  }, [authenticatedUser, location.state, navigate]);

  useEffect(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setErrorMessage('Enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      await requestOtp(normalizedEmail);
      setSubmittedEmail(normalizedEmail);
      setEmail(normalizedEmail);
      setOtp('');
      setAuthStep('otp');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setStatusMessage('Your sign-in code is on the way.');
    } catch (error) {
      setErrorMessage(toFriendlyAuthError(error, 'We could not send a sign-in code right now.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();

    const trimmedOtp = otp.trim();

    if (trimmedOtp.length < 6) {
      setErrorMessage('Enter the 6-digit code from your email.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setStatusMessage('');
    setAuthStep('loading');

    try {
      await verifyOtp({ email: submittedEmail, token: trimmedOtp });
      navigate(resolvePostAuthRoute(location.state), { replace: true });
    } catch (error) {
      setAuthStep('otp');
      setErrorMessage(toFriendlyAuthError(error, 'We could not verify that code. Try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!submittedEmail || resendCooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      await resendOtp(submittedEmail);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setStatusMessage('A fresh code is on its way.');
    } catch (error) {
      setErrorMessage(toFriendlyAuthError(error, 'We could not resend the code right now.'));
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = () => {
    setAuthStep('email');
    setOtp('');
    setSubmittedEmail('');
    setEmail(getDefaultLoginEmail());
    setErrorMessage('');
    setStatusMessage('');
    setResendCooldown(0);
  };

  const isBusy = isSubmitting || isResending;
  const isBootLoading = authLoading && !authenticatedUser && authStep === 'email';

  return (
    <div
      className="login-background cq-login-shell"
      style={{
        '--cq-login-bg': `url(${process.env.PUBLIC_URL}/images/bg_artdeco_pattern.png)`,
        '--cq-login-brass': `url(${process.env.PUBLIC_URL}/images/brass_plate_button.png)`,
      }}
    >
      <div className="app-container login-container cq-login-container">
        <header className="cq-login-header">
          <h1 className="cq-login-title">CRINGE QUEST</h1>
          <p className="cq-login-subtitle">An Evening of Uncomfortable Diversions</p>
        </header>

        <div className="cq-login-medallion" aria-hidden="true">
          <img
            className="cq-login-medallion__image"
            src="/images/jack_ass_medallion.png"
            alt=""
            width="160"
            height="160"
          />
        </div>

        <section className="cq-login-panel">
          {authStep === 'loading' ? (
            <>
              <h2 className="cq-login-panel__title">Signing you in...</h2>
              <p className="cq-login-panel__copy">
                We&apos;re unlocking the parlor and getting your account ready.
              </p>
            </>
          ) : (
            <>
              <h2 className="cq-login-panel__title">
                {authStep === 'otp' ? 'Enter your code' : 'Get started'}
              </h2>
              <p className="cq-login-panel__copy">
                {authStep === 'otp'
                  ? `We sent a sign-in code to ${maskedEmail}.`
                  : 'Enter your email and we’ll send you a sign-in code.'}
              </p>
            </>
          )}

          {!hasSupabaseConfig ? (
            <p className="cq-login-error" role="alert">
              Supabase auth is not configured for this environment.
            </p>
          ) : null}

          {errorMessage ? (
            <p className="cq-login-error" role="alert">
              {errorMessage}
            </p>
          ) : null}

          {statusMessage ? (
            <p className="cq-login-status" role="status">
              {statusMessage}
            </p>
          ) : null}

          {authStep === 'email' ? (
            <form onSubmit={handleEmailSubmit} className="cq-login-form">
              <label className="cq-login-field">
                <span className="cq-login-field__label">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="cq-login-input"
                  disabled={isBusy || isBootLoading || !hasSupabaseConfig}
                />
              </label>
              <button
                type="submit"
                className="cq-login-brass-button"
                disabled={isBusy || isBootLoading || !hasSupabaseConfig}
              >
                <span className="cq-login-brass-button__plate" aria-hidden="true" />
                <span className="cq-login-brass-button__label">
                  {isSubmitting || isBootLoading ? 'Sending...' : 'Continue'}
                </span>
              </button>
            </form>
          ) : null}

          {authStep === 'otp' ? (
            <form onSubmit={handleVerifySubmit} className="cq-login-form">
              <label className="cq-login-field">
                <span className="cq-login-field__label">Code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\s+/g, '').slice(0, 6))}
                  placeholder="123456"
                  required
                  maxLength={6}
                  className="cq-login-input cq-login-input--otp"
                  disabled={isBusy}
                />
              </label>
              <button type="submit" className="cq-login-brass-button" disabled={isBusy}>
                <span className="cq-login-brass-button__plate" aria-hidden="true" />
                <span className="cq-login-brass-button__label">
                  {isSubmitting ? 'Verifying...' : 'Verify'}
                </span>
              </button>
              <div className="cq-login-actions">
                <button
                  type="button"
                  className="cq-login-text-button"
                  onClick={handleResend}
                  disabled={isBusy || resendCooldown > 0}
                >
                  {isResending
                    ? 'Resending...'
                    : resendCooldown > 0
                      ? `Resend code in ${resendCooldown}s`
                      : 'Resend code'}
                </button>
                <button
                  type="button"
                  className="cq-login-text-button"
                  onClick={handleChangeEmail}
                  disabled={isBusy}
                >
                  Change email
                </button>
              </div>
            </form>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
